var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import PropTypes from 'prop-types';
import { Manager, Popper, Reference } from 'react-popper';
import clsx from 'clsx';
import { createForkRef } from '../utils/helpers/reactHelpers';
const Tooltip = (props) => {
    const { title, content, actions, onActions, children, trigger, className: classNameProp, id, onClose, onOpen, open: openProp, placement: placementProp, invertedBg, _variant: variant, _TooltipComp: TooltipComp } = props, other = __rest(props, ["title", "content", "actions", "onActions", "children", "trigger", "className", "id", "onClose", "onOpen", "open", "placement", "invertedBg", "_variant", "_TooltipComp"]);
    const [openState, setOpenState] = React.useState(false);
    const { current: isControlled } = React.useRef(openProp != null);
    const nodeRef = React.useRef(null);
    const popperRef = React.useRef(null);
    const isOpen = React.useCallback(() => {
        let isTooltipOpen = false;
        if (variant === 'modal' || title !== '') {
            isTooltipOpen = isControlled ? openProp : openState;
        }
        return isTooltipOpen;
    }, [variant, title, isControlled, openProp, openState]);
    const handleOpen = React.useCallback((event) => {
        if (!isControlled && !openState) {
            setOpenState(true);
        }
        if (onOpen) {
            onOpen(event);
        }
    }, [isControlled, openState, onOpen]);
    const handleClose = React.useCallback((event) => {
        if (!isControlled) {
            setOpenState(false);
        }
        if (onClose) {
            onClose(event);
        }
    }, [isControlled, onClose]);
    const handleClick = React.useCallback((event) => {
        const childrenProps = children.props;
        if (childrenProps.onClick) {
            childrenProps.onClick(event);
        }
        handleOpen(event);
    }, [children.props, handleOpen]);
    const handleDocumentClick = React.useCallback((event) => {
        if (variant === 'modal') {
            return;
        }
        if (nodeRef.current && nodeRef.current.contains(event.target)) {
            return;
        }
        if (popperRef.current &&
            variant === 'dialog' &&
            popperRef.current.contains(event.target)) {
            return;
        }
        if (isOpen()) {
            handleClose(event);
        }
    }, [isOpen, handleClose, variant]);
    const handleEnter = React.useCallback((event) => {
        const childrenProps = children.props;
        if (event.type === 'mouseenter' && childrenProps.onMouseEnter) {
            childrenProps.onMouseEnter(event);
        }
        handleOpen(event);
    }, [children.props, handleOpen]);
    const handleLeave = React.useCallback((event) => {
        const childrenProps = children.props;
        if (event.type === 'blur' && childrenProps.onBlur) {
            childrenProps.onBlur(event);
        }
        else if (event.type === 'mouseleave' && childrenProps.onMouseLeave) {
            childrenProps.onMouseLeave(event);
        }
        handleClose(event);
    }, [children.props, handleClose]);
    const handleFocus = React.useCallback((event) => {
        const childrenProps = children.props;
        if (event.type === 'focus' && childrenProps.onFocus) {
            childrenProps.onFocus(event);
        }
        handleEnter(event);
    }, [children.props, handleEnter]);
    React.useEffect(() => {
        if (trigger === 'click') {
            window.document.body.addEventListener('click', handleDocumentClick);
        }
        return () => {
            window.document.body.removeEventListener('click', handleDocumentClick);
        };
    }, [trigger, handleDocumentClick]);
    const updateArrowStyle = (arrowProps) => {
        if (popperRef.current) {
            const { left, top } = arrowProps.style;
            const popperElement = popperRef.current;
            if (left !== '') {
                if (left <= 4) {
                    arrowProps.style.left = '0.25rem';
                }
                else if (popperElement.offsetWidth -
                    popperElement.getElementsByClassName('_ndsTooltip__arrow')[0].offsetWidth -
                    Number(left) <=
                    4) {
                    arrowProps.style.left = `calc(${arrowProps.style.left / 16}rem - 0.25rem)`;
                }
            }
            else if (top <= 4) {
                arrowProps.style.top = '0.25rem';
            }
            else if (popperElement.offsetHeight -
                popperElement.getElementsByClassName('_ndsTooltip__arrow')[0].offsetHeight -
                Number(top) <=
                4) {
                arrowProps.style.top = `calc(${arrowProps.style.top / 16}rem - 0.25rem)`;
            }
        }
    };
    const getChildrenProps = () => {
        const childrenProps = children.props || {};
        const derivedChildrenProps = Object.assign(Object.assign(Object.assign({ 'aria-describedby': isOpen() ? id : null }, childrenProps), other), { className: clsx(childrenProps.className, {
                _ndsTooltip__reference: isOpen()
            }) });
        if (trigger === 'click') {
            derivedChildrenProps.onClick = handleClick;
        }
        else if (trigger === 'hover') {
            derivedChildrenProps.onMouseEnter = handleEnter;
            derivedChildrenProps.onMouseLeave = handleLeave;
            derivedChildrenProps.onFocus = handleFocus;
            derivedChildrenProps.onBlur = handleLeave;
            derivedChildrenProps.onClick = handleEnter;
        }
        return derivedChildrenProps;
    };
    const RenderTooltipComponent = (ref, style, placement, arrowProps) => {
        return (React.createElement(TooltipComp, { title: title, forwardedRef: createForkRef(ref, popperRef), rootStyle: style, placement: placement, arrowProps: arrowProps, className: classNameProp, content: content, actions: actions, onActions: onActions, onClose: handleClose, invertedBg: invertedBg }));
    };
    const modifiers = {
        offset: {
            offset: `0,${variant === 'text' ? '12' : '17'},${variant === 'text' ? '12' : '17'}`
        }
    };
    const PopperElement = (React.createElement(Popper, { placement: placementProp, modifiers: modifiers }, ({ ref, style, placement, arrowProps }) => {
        updateArrowStyle(arrowProps);
        return RenderTooltipComponent(ref, style, placement, arrowProps);
    }));
    return (React.createElement(Manager, null,
        React.createElement(Reference, null, ({ ref }) => {
            return React.cloneElement(children, Object.assign({ ref: createForkRef(children.ref, ref, nodeRef) }, getChildrenProps()));
        }),
        isOpen() && PopperElement));
};
Tooltip.propTypes = {
    title(props, propName, componentName) {
        const { _variant: variant } = props;
        const { [propName]: propValue } = props;
        if ((variant === 'dialog' || variant === 'text') && !propValue) {
            return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. \`title\` property must be provided for for \`TooltipDialog\` Or \`TooltipText\`.`);
        }
        return null;
    },
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    open: PropTypes.bool,
    _variant: PropTypes.oneOf(['text', 'dialog', 'modal']),
    actions: PropTypes.arrayOf(PropTypes.string),
    onActions: PropTypes.arrayOf(PropTypes.func),
    children: PropTypes.element,
    trigger(props, propName, componentName) {
        const { _variant: variant } = props;
        const { [propName]: propValue } = props;
        if ((variant === 'dialog' || variant === 'modal') && propValue !== 'click') {
            return new Error(`Invalid value \`${propValue}\` of prop \`${propName}\` supplied to \`${componentName}\`. trigger property must be \`click\` for \`TooltipDialog\` Or \`TooltipModal\`.`);
        }
        if (!['click', 'hover'].includes(propValue)) {
            return new Error(`Invalid value \`${propValue}\` of prop \`${propName}\` supplied to \`${componentName}\`. validation failed.`);
        }
        return null;
    },
    className: PropTypes.string,
    placement: PropTypes.oneOf([
        'top-start',
        'top',
        'top-end',
        'right-start',
        'right',
        'right-end',
        'bottom-end',
        'bottom',
        'bottom-start',
        'left-end',
        'left',
        'left-start'
    ]),
    invertedBg: PropTypes.bool,
    _TooltipComp: PropTypes.any
};
Tooltip.defaultProps = {
    title: null,
    content: null,
    onClose: undefined,
    onOpen: undefined,
    open: null,
    _variant: 'text',
    actions: [],
    onActions: [],
    children: null,
    trigger: 'hover',
    className: null,
    placement: 'top',
    _TooltipComp: null,
    invertedBg: null
};
export { Tooltip as TooltipRootComp };
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map